import { Check, X, Forward, Trash2, Edit } from 'lucide-react';

const AdminApplications = ({
  applications,
  onApprove,
  onReject,
  onEdit,
  onResend,
  onForward,
  onDelete,
}) => {
  if (applications.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400" data-testid="admin-applications-empty">
        No partner applications yet
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-applications">
      {applications.map((app) => (
        <div
          key={app.id}
          className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700"
          data-testid={`admin-application-${app.id.slice(0, 8)}`}
        >
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">{app.company_name}</h3>
              <p className="text-gray-400">{app.email}</p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm font-semibold ${
                app.status === 'pending'
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : app.status === 'approved'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}
            >
              {app.status}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
            <div>
              <span className="text-gray-400">Business Type:</span>
              <p className="text-white font-medium">{app.business_type}</p>
            </div>
            <div>
              <span className="text-gray-400">Country:</span>
              <p className="text-white font-medium">{app.country}</p>
            </div>
            <div>
              <span className="text-gray-400">Years in Business:</span>
              <p className="text-white font-medium">{app.years_in_business}</p>
            </div>
            <div>
              <span className="text-gray-400">Est. Volume:</span>
              <p className="text-white font-medium">{app.estimated_monthly_volume}</p>
            </div>
          </div>

          {app.additional_notes && (
            <div className="mb-4">
              <span className="text-gray-400 text-sm">Notes:</span>
              <p className="text-white">{app.additional_notes}</p>
            </div>
          )}

          {app.status === 'pending' && (
            <div className="flex gap-3">
              <button
                onClick={() => onApprove(app.id)}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                data-testid={`admin-application-approve-${app.id.slice(0, 8)}`}
              >
                <Check size={18} />
                Approve
              </button>
              <button
                onClick={() => onReject(app.id)}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                data-testid={`admin-application-reject-${app.id.slice(0, 8)}`}
              >
                <X size={18} />
                Reject
              </button>
            </div>
          )}

          <div className="flex gap-3 mt-3">
            <button
              onClick={() => onForward(app.id)}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
              title="Forward to info@ewss.co.za"
              data-testid={`admin-application-forward-${app.id.slice(0, 8)}`}
            >
              <Forward size={18} />
              Forward
            </button>
            <button
              onClick={() => onDelete(app.id)}
              className="flex-1 px-4 py-2 bg-red-600/80 hover:bg-red-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
              title="Delete application"
              data-testid={`admin-application-delete-${app.id.slice(0, 8)}`}
            >
              <Trash2 size={18} />
              Delete
            </button>
          </div>

          {app.status === 'approved' && (
            <div className="mt-3 space-y-3">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <p className="text-green-400 text-sm font-semibold mb-2">✓ Approved Partner</p>
                {app.approved_brands && app.approved_brands.length > 0 ? (
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-300">
                      <span className="text-gray-400">Brands:</span>{' '}
                      {app.approved_brands.join(', ')}
                    </p>
                    {app.brand_discounts && (
                      <p className="text-gray-300">
                        <span className="text-gray-400">Discounts:</span>{' '}
                        {Object.entries(app.brand_discounts)
                          .map(([brand, disc]) => `${brand}: ${disc}%`)
                          .join(' | ')}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-sm">
                    <p className="text-yellow-400 mb-2">
                      ⚠️ No brand access configured (old approval)
                    </p>
                    <p className="text-gray-400 text-xs">
                      Click "Edit Brands" to configure brand access and discounts
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => onEdit(app)}
                  className="flex-1 px-4 py-2 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 transition-colors flex items-center justify-center gap-2"
                  data-testid={`admin-application-edit-${app.id.slice(0, 8)}`}
                >
                  <Edit size={18} />
                  Edit Brands & Discounts
                </button>
                <button
                  onClick={() => onResend(app.id)}
                  className="flex-1 px-4 py-2 border-2 border-ewss-cyan text-ewss-cyan font-semibold rounded-lg hover:bg-ewss-cyan/10 transition-all duration-300"
                  data-testid={`admin-application-resend-${app.id.slice(0, 8)}`}
                >
                  📧 Resend Credentials
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AdminApplications;
